import { Check, Copy, Eye, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { confirmModal } from '@/components/ui/modal'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { HR_WRITE_PERM } from '../../permissions'
import { apiErrorMessage } from '@/i18n'
import { useArchiveEmployee, useChangeWorkStatus } from '../api'
import type { Personel, PersonelDurum } from '../types'

const DURUMLAR: PersonelDurum[] = ['aktif', 'izinli', 'deneme']

const DOT: Record<PersonelDurum, string> = {
  aktif: 'bg-success',
  izinli: 'bg-warning',
  deneme: 'bg-info',
}

export function PersonelRowActions({ personel, variant = 'row' }: { personel: Personel; variant?: 'row' | 'profile' }) {
  const profile = variant === 'profile'
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = useAuth()
  const archive = useArchiveEmployee()
  const changeStatus = useChangeWorkStatus()
  const canWrite = can(HR_WRITE_PERM)
  const { id, ad, email, durum } = personel

  const copyEmail = () => {
    void navigator.clipboard?.writeText(email)
    toast.success(t('hr.personel.rowActions.emailCopied', { email }))
  }

  const remove = async () => {
    const ok = await confirmModal({
      title: t('confirm.personelDelete.title'),
      description: t('confirm.personelDelete.body', { name: ad }),
      confirmLabel: t('hr.personel.rowActions.remove'),
      destructive: true,
    })
    if (!ok) return
    try {
      await archive.mutateAsync(id)
      toast.success(t('hr.personel.rowActions.removed', { name: ad }))
      if (profile) navigate('/hr/personel')
    } catch {
      toast.error(t('common.error', { defaultValue: 'İşlem tamamlanamadı' }))
    }
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={profile ? 'outline' : 'ghost'}
          size="icon"
          className={cn(
            'data-[state=open]:bg-muted data-[state=open]:text-foreground',
            profile ? 'bg-card shadow-xs' : 'size-8 text-muted-foreground',
          )}
          aria-label={t(profile ? 'hr.personel.detail.dahaFazla' : 'hr.personel.columns.islem')}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl" onClick={(e) => e.stopPropagation()}>
        {profile ? null : (
          <DropdownMenuItem onSelect={() => navigate(`/hr/personel/${id}`)}>
            <Eye />
            {t('hr.personel.rowActions.view')}
          </DropdownMenuItem>
        )}
        {canWrite ? (
          <DropdownMenuItem onSelect={() => navigate(`/hr/personel/${id}/duzenle`)}>
            <Pencil />
            {t('hr.personel.rowActions.edit')}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={copyEmail}>
          <Copy />
          {t('hr.personel.rowActions.copyEmail')}
        </DropdownMenuItem>
        {canWrite ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t('hr.personel.rowActions.changeStatus')}</DropdownMenuLabel>
            {DURUMLAR.map((d) => (
              <DropdownMenuItem
                key={d}
                disabled={changeStatus.isPending}
                onSelect={() => {
                  if (d === durum) return
                  changeStatus.mutate(
                    { id, durum: d },
                    {
                      onSuccess: () => toast.success(t('hr.personel.rowActions.statusChanged', { name: ad, durum: t(`hr.personel.durum.${d}`) })),
                      onError: (e) => toast.error(apiErrorMessage(e)),
                    },
                  )
                }}
                className={cn(d === durum && 'font-medium')}
              >
                <span className={cn('ml-1 mr-1 size-2 shrink-0 rounded-full', DOT[d])} aria-hidden />
                <span className="flex-1">{t(`hr.personel.durum.${d}`)}</span>
                {d === durum ? <Check className="!text-primary" /> : null}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => void remove()}
              className="text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive [&_svg]:text-destructive"
            >
              <Trash2 />
              {t('hr.personel.rowActions.remove')}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
